/**
 * BlockingErrorModal
 * A full-screen modal overlay for critical errors that require user acknowledgment.
 *
 * Features:
 * - Blocks all interaction with the app until acknowledged
 * - Cannot be dismissed via click-away or ESC key
 * - Locks body scroll while visible
 * - Auto-focuses the acknowledge button for accessibility
 */

import React, { useEffect, useRef } from 'react';
import { useBlockingErrorStore } from '@/stores/blockingErrorStore';

const BlockingErrorModal: React.FC = () => {
  const { message, title, clear } = useBlockingErrorStore();
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Lock body scroll and trap focus when modal is visible
  useEffect(() => {
    if (!message) return;

    // Store previous overflow style
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Focus the acknowledge button
    setTimeout(() => {
      buttonRef.current?.focus();
    }, 0);

    // Block ESC key
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [message]);

  if (!message) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70"
      role="presentation"
      // Prevent click-through to underlying content
      onClick={(e) => e.stopPropagation()}
    >
      {/* Modal container */}
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="blocking-error-title"
        aria-describedby="blocking-error-message"
        className="w-full max-w-lg mx-4 rounded-xl bg-white shadow-2xl animate-in fade-in zoom-in duration-200"
      >
        {/* Header with error icon */}
        <div className="flex items-center gap-3 p-6 pb-4 border-b border-gray-100">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <i className="fas fa-exclamation-triangle text-red-600 text-xl"></i>
          </div>
          <h2
            id="blocking-error-title"
            className="text-lg font-semibold text-gray-900"
          >
            {title}
          </h2>
        </div>

        {/* Message body */}
        <div className="p-6">
          <p
            id="blocking-error-message"
            className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap"
          >
            {message}
          </p>
        </div>

        {/* Footer with acknowledge button */}
        <div className="flex justify-end gap-3 p-6 pt-4 bg-gray-50 rounded-b-xl">
          <button
            ref={buttonRef}
            onClick={clear}
            className="px-5 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
          >
            <i className="fas fa-check mr-2"></i>
            Acknowledge
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlockingErrorModal;
